import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Activity, AlertTriangle, Bot, MessageSquare, Paperclip, UserRound } from "lucide-react";
import { ROUTES } from "@/app/routes";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import {
  postApiSessionsAdminBootstrap,
  postApiSessionsAdminGet,
  postApiSessionsBootstrap,
  postApiSessionsGet,
} from "@/lib/api/generated/sessions/sessions";
import type {
  SessionAdminBootstrapRequest,
  SessionAdminGetRequest,
  SessionBootstrapResponse,
  SessionGetResponse,
} from "@/lib/api/generated/control-api.schemas";
import { SessionList, type SessionListEntry } from "./conversation-session-list";
import { ConversationMessageThread } from "./conversation-message-thread";
import { getLatestConversationIssue } from "./conversation-status.js";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type { SessionLiveRuntimeEvent } from "../../../../../packages/shared-types/src/index.js";
import {
  sessionQueryKeys,
  useSessionLiveUpdates,
} from "./use-session-live-updates";

interface AdminUserSessionWorkspaceProps {
  targetUserId: string;
}

type ConversationRuntimeState = {
  events: SessionLiveRuntimeEvent[];
};

function normalizeTargetUserId(value?: string): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

async function fetchSessionsBootstrap(targetUserId?: string): Promise<SessionBootstrapResponse> {
  if (!targetUserId) {
    return await postApiSessionsBootstrap({});
  }

  const request: SessionAdminBootstrapRequest = { targetUserId };
  return await postApiSessionsAdminBootstrap(request);
}

async function fetchSessionDetail(
  sessionId: string,
  targetUserId?: string,
): Promise<SessionGetResponse> {
  if (!targetUserId) {
    return await postApiSessionsGet({ sessionId });
  }

  const request: SessionAdminGetRequest = {
    targetUserId,
    sessionId,
  };
  return await postApiSessionsAdminGet(request);
}

export function AdminUserSessionWorkspace({ targetUserId: targetUserIdInput }: AdminUserSessionWorkspaceProps) {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runtimeBySessionId, setRuntimeBySessionId] = useState<Record<string, ConversationRuntimeState>>({});

  const applyRuntimeEvent = useCallback((event: SessionLiveRuntimeEvent) => {
    setRuntimeBySessionId((state) => {
      const current = state[event.sessionId];
      const nextEvents = [...(current?.events ?? []), event]
        .filter(
          (candidate, index, entries) =>
            entries.findIndex(
              (entry) =>
                entry.runId === candidate.runId &&
                entry.seq === candidate.seq &&
                entry.sessionId === candidate.sessionId,
            ) === index,
        )
        .sort((left, right) => {
          if (left.ts !== right.ts) {
            return left.ts - right.ts;
          }
          if (left.runId !== right.runId) {
            return left.runId.localeCompare(right.runId);
          }
          return left.seq - right.seq;
        })
        .slice(-64);

      return {
        ...state,
        [event.sessionId]: {
          events: nextEvents,
        },
      };
    });
  }, []);

  const clearRuntimeEvents = useCallback((sessionId: string) => {
    setRuntimeBySessionId((state) => {
      if (!Object.hasOwn(state, sessionId)) {
        return state;
      }
      const next = { ...state };
      delete next[sessionId];
      return next;
    });
  }, []);
  const agentsQuery = useGetApiAgents({ query: { enabled: Boolean(tenantId) } });
  const targetUserId = normalizeTargetUserId(targetUserIdInput);
  if (!targetUserId) {
    throw new Error("targetUserId is required for admin session workspace.");
  }

  const routeSessionId = searchParams.get("session") || undefined;
  const bootstrapQuery = useQuery({
    queryKey: sessionQueryKeys.bootstrap(tenantId!, targetUserId),
    queryFn: async () => await fetchSessionsBootstrap(targetUserId),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const sessionEntries = useMemo<SessionListEntry[]>(() => {
    const bootstrap = bootstrapQuery.data;
    if (!bootstrap) {
      return [];
    }

    const visibleAgentsById = new Map(
      bootstrap.visibleAgents.map((agent) => [agent.agentId, agent]),
    );
    return bootstrap.sessions.map((session) => {
      const agent = visibleAgentsById.get(session.agentId);
      const runtimeEvents = runtimeBySessionId[session.sessionId]?.events ?? [];
      return {
        sessionId: session.sessionId,
        agentId: session.agentId,
        title: session.title || agent?.name || session.agentName || session.agentId,
        subtitle: session.lastMessageText?.trim() || agent?.description || session.agentId,
        updatedAt: session.lastMessageAt || session.updatedAt,
        hasMessages: session.messageCount > 0,
        hasLiveRuntimeActivity: isRuntimeSessionActive(runtimeEvents),
      };
    });
  }, [bootstrapQuery.data, runtimeBySessionId]);
  const liveSessionIds = useMemo(
    () => sessionEntries.map((entry) => entry.sessionId),
    [sessionEntries],
  );

  const selectedConversationId = sessionEntries.some((entry) => entry.sessionId === routeSessionId)
    ? routeSessionId
    : sessionEntries[0]?.sessionId;

  useEffect(() => {
    if (selectedConversationId && searchParams.get("session") !== selectedConversationId) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("session", selectedConversationId);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [searchParams, selectedConversationId, setSearchParams, targetUserId]);
  const sessionQuery = useQuery({
    queryKey: sessionQueryKeys.detail(tenantId!, selectedConversationId ?? "none", targetUserId),
    queryFn: async () => await fetchSessionDetail(selectedConversationId!, targetUserId),
    enabled: Boolean(tenantId && selectedConversationId),
    refetchOnWindowFocus: false,
  });
  const selectedSession =
    sessionEntries.find((entry) => entry.sessionId === selectedConversationId) || null;
  const session = sessionQuery.data;
  const latestIssue = getLatestConversationIssue(session?.messages ?? []);
  const runtimeEvents = selectedConversationId
    ? runtimeBySessionId[selectedConversationId]?.events ?? []
    : [];
  useSessionLiveUpdates({
    tenantId,
    sessionIds: liveSessionIds,
    targetUserId,
    onRuntimeEvent: applyRuntimeEvent,
    onAssistantMessage: clearRuntimeEvents,
  });

  if (agentsQuery.isLoading || bootstrapQuery.isLoading) {
    return <ConversationLoadingState />;
  }

  if ((agentsQuery.data?.length ?? 0) === 0) {
    return (
      <EmptyState
        title="No agents yet"
        description="Conversations run through an agent. Create one first, then use this workspace like ChatGPT with reusable session files."
        action={{
          label: "Create first agent",
          onClick: () => navigate(ROUTES.tenantAgentNew(tenantId!)),
        }}
      />
    );
  }

  if (bootstrapQuery.error) {
    return (
      <EmptyState
        title="Conversation workspace unavailable"
        description={
          bootstrapQuery.error instanceof Error
            ? bootstrapQuery.error.message
            : "Failed to load sessions."
        }
      />
    );
  }

  return (
    <div className="grid h-[calc(100vh-10rem)] min-h-[42rem] overflow-hidden rounded-lg border bg-background xl:grid-cols-[340px_minmax(0,1fr)]" style={{ borderColor: "var(--border)" }}>
      <aside className="flex min-h-0 flex-col border-r bg-muted/20" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3 border-b p-4" style={{ borderColor: "var(--border)" }}>
          <Avatar className="h-9 w-9">
            <AvatarFallback>
              <UserRound size={16} />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
              User sessions
            </div>
            <div className="truncate text-xs" style={{ color: "var(--muted-foreground)" }}>
              {targetUserId}
            </div>
          </div>
          <Badge variant="outline">{sessionEntries.length}</Badge>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {sessionEntries.length ? (
            <SessionList
              sessions={sessionEntries}
              selectedConversationId={selectedConversationId}
              onSelect={(sessionId) => {
                const nextSearchParams = new URLSearchParams(searchParams);
                nextSearchParams.set("session", sessionId);
                setSearchParams(nextSearchParams, { replace: true });
              }}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
              <MessageSquare size={18} style={{ color: "var(--muted-foreground)" }} />
              <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                No sessions
              </div>
              <div className="max-w-56 text-xs leading-5" style={{ color: "var(--muted-foreground)" }}>
                This user has no stored conversations to monitor.
              </div>
            </div>
          )}
        </div>
      </aside>

      <section className="flex min-h-0 flex-col">
        <header className="flex items-center justify-between gap-4 border-b px-5 py-4" style={{ borderColor: "var(--border)" }}>
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback>
                <Bot size={16} />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold" style={{ color: "var(--text-strong)" }}>
                {session?.session.title || selectedSession?.title || "No conversation selected"}
              </h3>
              <div className="mt-1 flex min-w-0 items-center gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                {selectedSession ? (
                  <>
                    <span className="truncate">{session?.session.agentName || selectedSession.agentId}</span>
                    <span aria-hidden="true">/</span>
                    <span>{session?.messages.length ?? 0} messages</span>
                  </>
                ) : (
                  <span>Stored conversations only</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {latestIssue ? (
              <Badge variant="error" className="gap-1">
                <AlertTriangle size={12} />
                issue
              </Badge>
            ) : null}
            {runtimeEvents.length ? (
              <Badge variant="running" className="gap-1">
                <Activity size={12} />
                live
              </Badge>
            ) : null}
            {(session?.files.length ?? 0) + (session?.artifacts.length ?? 0) > 0 ? (
              <Badge variant="outline" className="gap-1">
                <Paperclip size={12} />
                {(session?.files.length ?? 0) + (session?.artifacts.length ?? 0)}
              </Badge>
            ) : null}
          </div>
        </header>

        {latestIssue ? (
          <div className="border-b px-5 py-3 text-sm" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--danger) 8%, transparent)" }}>
            <div className="whitespace-pre-wrap leading-6" style={{ color: "var(--text-strong)" }}>{latestIssue}</div>
          </div>
        ) : null}

        <div className="min-h-0 flex-1">
          {selectedSession ? (
            <ConversationMessageThread
              messages={session?.messages ?? []}
              runtimeEvents={runtimeEvents}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <MessageSquare size={20} style={{ color: "var(--muted-foreground)" }} />
              <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                No conversations for this user
              </div>
              <div className="max-w-md text-sm leading-6" style={{ color: "var(--muted-foreground)" }}>
                Admin monitoring only shows conversations that already exist.
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function isRuntimeSessionActive(
  events: Array<{ stream: string; data: Record<string, unknown> }>,
): boolean {
  const latestEvent = events.at(-1);
  if (!latestEvent) {
    return false;
  }
  if (latestEvent.stream !== "lifecycle") {
    return true;
  }
  const phase = typeof latestEvent.data.phase === "string" ? latestEvent.data.phase : undefined;
  return phase !== "end" && phase !== "error";
}

function ConversationLoadingState() {
  return (
    <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
      <div className="h-[calc(100vh-11rem)] rounded-md border" style={{ borderColor: "var(--border)" }} />
      <div className="grid gap-4">
        <div className="h-80 rounded-md border" style={{ borderColor: "var(--border)" }} />
        <div className="h-56 rounded-md border" style={{ borderColor: "var(--border)" }} />
      </div>
    </div>
  );
}
